import autobind from 'class-autobind';
import * as React from 'react';
import FlipMove from 'react-flip-move';
import { compose, shallowEqual, toClass } from 'recompose';
import getClientEnvironment from '../../../getClientEnvironment';
import LoadingSpinner from '../../LoadingSpinner';
import { CompositeComponent } from '../../types';
import { withAnimationContextProvider } from '../../withAnimationContext';
import {
    ActivityChartContainerProps,
    ActivityInput,
    ActivityMapContainerProps,
    ActivityMapOrChartContainerProps,
    AthleteProps,
    CustomStreamType,
    SaveAthleteOptionsProps,
    StravaResolution,
    StravaStreamType,
    UnitsPreference
} from '../types';
import { withChartStreams, withMapStreams } from '../withActivityStreams';
import withSaveAthleteOptions from '../withSaveAthleteOptions';
import ActivityChartRow from './ActivityChart/ActivityChartRow';
import ActivityChartsToolbar from './ActivityChartsToolbar';
import LazyActivityMap, {
    LazyActivityMapPlaceholder
} from './ActivityMap/LazyActivityMap';

const config = getClientEnvironment();

const LazyActivityMapWithData = compose<any, ActivityMapContainerProps>(
    withMapStreams
)(LazyActivityMap);

const ActivityChartRowWithData = compose<any, any>(toClass, withChartStreams)(
    ActivityChartRow
);

/** Placeholder for the map and chart while the code is dynamically loading */
export const ActivityMapAndChartContainerPlaceholder = () => (
    <div className="activity-map-container">
        <LoadingSpinner className="map-loader" />
    </div>
);

type ActivityMapAndChartContainerProps = ActivityInput &
    AthleteProps &
    SaveAthleteOptionsProps;

export const createActivityMapAndChartContainer = (
    Map: CompositeComponent<ActivityMapContainerProps>,
    Chart: CompositeComponent<
        ActivityChartContainerProps & SaveAthleteOptionsProps & AthleteProps
    >
) => {
    return class ActivityMapAndChartContainer extends React.Component<
        ActivityMapAndChartContainerProps
    > {
        public constructor(props: ActivityMapAndChartContainerProps) {
            super(props);
            autobind(this);
        }

        public shouldComponentUpdate(
            nextProps: ActivityMapAndChartContainerProps,
            nextState: any
        ) {
            // The animation context provider will cause a re-render
            // on every frame if we don't check for changes in the props
            return !shallowEqual(this.props, nextProps);
        }

        public render() {
            const { id, error, loading } = this.props;

            const athlete = this.getAthleteFromProps(this.props);

            if (error) {
                return ActivityMapAndChartContainerPlaceholder;
            }

            const height = 140;

            const xAxisStreamTypes: (
                | StravaStreamType.distance
                | StravaStreamType.time)[] = [
                StravaStreamType.distance,
                StravaStreamType.time
            ];

            const yAxisStreamTypes = (
                (athlete && athlete.activityStreamTypes) ||
                []
            ).filter(
                t =>
                    t !== StravaStreamType.distance &&
                    t !== StravaStreamType.time
            );

            const selectedStreamType =
                (athlete && athlete.selectedActivityStreamType) || null;

            const athleteProps: AthleteProps & SaveAthleteOptionsProps = {
                activityStreamInfoList: this.props.activityStreamInfoList,
                addActivityStream: this.props.addActivityStream,
                error,
                loading,
                loggedInUser: this.props.loggedInUser,
                moveActivityStream: this.props.moveActivityStream,
                removeActivityStream: this.props.removeActivityStream,
                selectActivityStream: this.props.selectActivityStream,
                toggleAutoplayAnimation: this.props.toggleAutoplayAnimation
            };

            const mapAndChartProps: ActivityMapOrChartContainerProps = {
                autoplayAnimation:
                    (athlete && athlete.autoplayAnimation) || false,
                color: config.THEME_COLOR,
                resolution:
                    athlete && athlete.resolution
                        ? athlete.resolution
                        : StravaResolution.medium,
                unitsPreference:
                    athlete && athlete.unitsPreference
                        ? athlete.unitsPreference
                        : UnitsPreference.imperial,
                xAxisStreamTypes,
                yAxisStreamType: selectedStreamType
            };

            return (
                <>
                    <div
                        className="activity-map-container"
                        id="activity-map-container"
                    >
                        {loading ? (
                            <LazyActivityMapPlaceholder />
                        ) : (
                            <Map
                                id={id}
                                {...mapAndChartProps}
                                yAxisStreamType={selectedStreamType}
                                showNavigationControls={true}
                                offsetRightPercent={0}
                                offsetRightBreakpoint={991}
                            />
                        )}
                    </div>
                    <ActivityChartsToolbar id={id} {...athleteProps} />
                    <div>
                        <FlipMove duration={300}>
                            {yAxisStreamTypes.map((t, i) => (
                                <Chart
                                    key={t}
                                    id={id}
                                    {...mapAndChartProps}
                                    {...athleteProps}
                                    yAxisStreamType={t}
                                    name={this.getNameForStreamType(t)}
                                    height={height}
                                    isSelected={selectedStreamType === t}
                                    isTopItem={i === 0}
                                    isBottomItem={
                                        i === yAxisStreamTypes.length - 1
                                    }
                                />
                            ))}
                        </FlipMove>
                    </div>
                </>
            );
        }

        private getNameForStreamType(
            type: StravaStreamType | CustomStreamType
        ) {
            const streamInfo =
                this.props.activityStreamInfoList &&
                this.props.activityStreamInfoList.activityStreamInfoList;

            if (!streamInfo) {
                return '';
            }

            const info = streamInfo.find(s => s.type === type);

            return (info && info.friendlyName) || '';
        }

        private getAthleteFromProps(props: AthleteProps) {
            return props.loggedInUser && props.loggedInUser.user
                ? props.loggedInUser.user.athlete
                : undefined;
        }
    };
};

const MapAndChartContainer = createActivityMapAndChartContainer(
    LazyActivityMapWithData,
    ActivityChartRowWithData
);

export default compose<
    ActivityInput & AthleteProps & SaveAthleteOptionsProps,
    ActivityInput
>(withSaveAthleteOptions, withAnimationContextProvider)(MapAndChartContainer);



// WEBPACK FOOTER //
// ./src/client/components/Strava/ActivityScene/ActivityMapAndChartContainer.tsx